// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useLayoutEffect, useRef, type KeyboardEvent } from 'react'

import {
  Button,
  DarkTooltipContent,
  Input,
  Label,
  MarkdownViewer,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { MarkdownEditor } from '@/components/ui/markdown-editor/markdown-editor'
import { Edit, Info, Loader2, Save, X } from 'lucide-react'

import type { KbSchema, KnowledgeBaseItem } from '../types'
import type { KnowledgeBaseSettings } from '../hooks/useKnowledgeBaseSettings'
import { useItemDraft } from '../hooks/useItemDraft'
import { parseScore, SCORE_MAX, SCORE_MIN } from '../utils/validation'
import type { ItemListUpdatePayload } from './ItemList'
import { SchemaFieldsEditor } from './SchemaFieldsEditor'
import { SchemaFieldsView } from './SchemaFieldsView'
import { renderContentFromFields } from './fieldsRenderer'
import { GuidanceItemCard } from './GuidanceItemCard'
import { GuidanceItemEditor } from './GuidanceItemEditor'

interface ItemRowProps {
  item: KnowledgeBaseItem
  editing: boolean
  readOnly: boolean
  disabled: boolean
  saving: boolean
  settings: KnowledgeBaseSettings
  schema?: KbSchema | null
  onEdit: () => void
  onCancelEdit: () => void
  onSave: (payload: ItemListUpdatePayload) => Promise<void>
  onDelete: () => void
  onError: (message: string) => void
}

export function ItemRow({
  item,
  editing,
  readOnly,
  disabled,
  saving,
  settings,
  schema,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onError,
}: ItemRowProps): JSX.Element {
  const draft = useItemDraft()

  // Ref pattern to avoid including draft in deps (draft object recreated each render)
  const loadFromItemRef = useRef(draft.loadFromItem)
  loadFromItemRef.current = draft.loadFromItem

  // Load the item data when entering edit mode
  useLayoutEffect(() => {
    if (editing) {
      loadFromItemRef.current(item)
    }
  }, [editing, item])

  const scoreValid = parseScore(draft.score)
  const requiresScore = settings.mode === 'findings'
  const useSchema = !!schema && schema.fields.length > 0

  const handleSave = useCallback(async () => {
    if (!editing) return

    const parsedScore = parseScore(draft.score)
    if (requiresScore && parsedScore === null) {
      onError(`${settings.scoreLabel} must be a whole number between ${SCORE_MIN} and ${SCORE_MAX}.`)
      return
    }

    if (useSchema && schema) {
      const missing = schema.fields.find(
        f => f.required && !(draft.fields[f.key] ?? '').trim(),
      )
      if (missing) {
        onError(`${missing.label} is required.`)
        return
      }
    }

    const content = useSchema && schema
      ? renderContentFromFields(schema, draft.fields)
      : draft.content.trim()

    try {
      await onSave({
        content,
        score: requiresScore ? parsedScore : null,
        fields: useSchema ? draft.fields : null,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update item'
      onError(message)
    }
  }, [
    draft.content,
    draft.fields,
    draft.score,
    editing,
    onError,
    onSave,
    requiresScore,
    schema,
    settings.scoreLabel,
    useSchema,
  ])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      if (editing && (!requiresScore || scoreValid !== null)) {
        void handleSave()
      }
    }
    if (event.key === 'Escape' && editing) {
      onCancelEdit()
    }
  }, [editing, requiresScore, scoreValid, handleSave, onCancelEdit])

  const hasFields = useSchema && Object.values(draft.fields).some(v => v.trim().length > 0)
  const hasContent = draft.content.trim().length > 0
  const canSave = useSchema ? hasFields : hasContent

  if (editing && requiresScore && !useSchema) {
    return (
      <GuidanceItemEditor
        item={item}
        content={draft.content}
        score={draft.score}
        saving={saving}
        onContentChange={content => draft.update({ content })}
        onScoreChange={score => draft.update({ score })}
        onCancel={onCancelEdit}
        onSave={() => void handleSave()}
      />
    )
  }

  if (editing) {
    return (
      <li
        data-testid={`kb-row-${item.id}`}
        className="group rounded-xl border border-border/60 bg-card transition-all duration-200 hover:shadow-md hover:border-border hover:bg-card/80"
        onKeyDown={handleKeyDown}
      >
        <div className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {item.id}
              </span>
              <div className="text-sm text-slate-600 whitespace-nowrap">
                <kbd className="px-1.5 py-0.5 rounded bg-muted border">⌘</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-muted border ml-0.5">Enter</kbd>
                <span className="ml-1">save</span>
                <span className="mx-2 hidden sm:inline">•</span>
                <kbd className="px-1.5 py-0.5 rounded bg-muted border ml-2 sm:ml-0">Esc</kbd>
                <span className="ml-1">cancel</span>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="ghost" size="sm" onClick={onCancelEdit} disabled={saving} className="flex-1 sm:flex-none">
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => void handleSave()}
                disabled={saving || !canSave || (requiresScore && scoreValid === null)}
                className="flex-1 sm:flex-none"
                data-testid={`kb-item-save-${item.id}`}
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </div>

          {requiresScore && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {settings.scoreLabel}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Info className="h-3.5 w-3.5 text-muted-foreground" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-sm leading-relaxed">
                        {settings.scoreExplanation}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  value={draft.score}
                  type="number"
                  min={SCORE_MIN}
                  max={SCORE_MAX}
                  step={1}
                  onChange={event => draft.update({ score: event.target.value })}
                  disabled={saving || disabled}
                />
                {scoreValid === null && draft.score.trim().length > 0 ? (
                  <p className="text-[10px] text-destructive">
                    {settings.scoreLabel} must be a whole number between {SCORE_MIN} and {SCORE_MAX}.
                  </p>
                ) : (
                  <p className="text-[10px] text-muted-foreground">Whole numbers only ({SCORE_MIN}-{SCORE_MAX}).</p>
                )}
              </div>
            </div>
          )}

          <div className={saving || disabled ? 'pointer-events-none opacity-60' : ''}>
            {useSchema && schema ? (
              <SchemaFieldsEditor
                schema={schema}
                values={draft.fields}
                disabled={saving || disabled}
                onChange={draft.updateField}
              />
            ) : (
              <MarkdownEditor
                key={item.id}
                value={draft.content}
                onChange={(value: string) => draft.update({ content: value })}
                onKeyDown={handleKeyDown}
              />
            )}
          </div>
        </div>
      </li>
    )
  }

  const itemHasFields = !!item.fields && Object.keys(item.fields).length > 0

  if (requiresScore && !useSchema) {
    return (
      <GuidanceItemCard
        item={item}
        readOnly={readOnly}
        disabled={disabled}
        saving={saving}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    )
  }

  return (
    <li
      data-testid={`kb-row-${item.id}`}
      className="group rounded-xl border border-border/60 bg-card transition-all duration-200 hover:shadow-md hover:border-border hover:bg-card/80"
    >
      <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4">
        <div className="flex items-center gap-3 shrink-0">
          <span className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 px-2 py-0.5 text-[10px] sm:text-xs font-bold text-primary ring-1 ring-primary/20">
            {item.id}
          </span>
        </div>
        <div className="flex-1 min-w-0 space-y-2.5">
          <div className="max-h-96 overflow-y-auto">
            {useSchema && schema && itemHasFields ? (
              <SchemaFieldsView schema={schema} fields={item.fields ?? {}} />
            ) : (
              <MarkdownViewer content={item.content} />
            )}
          </div>

          {requiresScore && typeof item.score === 'number' && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1">
                {settings.scoreLabel}: <strong>{item.score}</strong>
              </span>
            </div>
          )}
        </div>
        {!readOnly && (
          <TooltipProvider delayDuration={300}>
            <div className="flex flex-col items-end gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Edit item"
                    variant="ghost"
                    size="sm"
                    onClick={onEdit}
                    className="h-8 w-8 p-0"
                    disabled={disabled || saving}
                    data-testid={`kb-item-edit-${item.id}`}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <DarkTooltipContent>Edit</DarkTooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Delete item"
                    variant="ghost"
                    size="sm"
                    onClick={onDelete}
                    className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                    disabled={disabled || saving}
                    data-testid={`kb-item-delete-${item.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <DarkTooltipContent>Delete</DarkTooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        )}
      </div>
    </li>
  )
}
